import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { getProjects, getProject } from '../../services/api';
import { getDefaultLLMConfig } from '../../constants/llmConfig';
import { DEFAULT_MODEL } from '../../constants/models';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import StepProjectSelect from './steps/StepProjectSelect';
import StepNameAndVariables from './steps/StepNameAndVariables';
import StepModelConfig from './steps/StepModelConfig';

const WIZARD_STEPS = [
  { id: 'projectSelect', title: 'Select Prompt' },
  { id: 'nameAndVariables', title: 'Details' },
  { id: 'modelConfig', title: 'Configure Models' },
];

const ComparisonWizard = ({ onComplete, onCancel }) => {
  const { currentWorkspace, getAvailableProviders } = useWorkspace();
  const [currentStep, setCurrentStep] = useState(0);
  
  const availableProviders = getAvailableProviders();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchProject, setSearchProject] = useState('');
  const [comparisonName, setComparisonName] = useState('');
  const [config1, setConfig1] = useState(getDefaultLLMConfig(DEFAULT_MODEL));
  const [config2, setConfig2] = useState(getDefaultLLMConfig('gpt-4o-mini'));
  const [inputValues, setInputValues] = useState({});

  const currentStepData = WIZARD_STEPS[currentStep];
  const isFirstStep = currentStep === 0;

  useEffect(() => {
    const loadProjects = async () => {
      if (!currentWorkspace) return;
      
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      const result = await getProjects(100, 0, workspaceId);
      if (result.success) {
        const completedProjects = result.data.filter(p => p.status === 'complete');
        setProjects(completedProjects);
      }
    };
    loadProjects();
  }, [currentWorkspace]);

  const handleSelectProject = async (project) => {
    const result = await getProject(project.id);
    if (result.success) {
      setSelectedProject(result.data);
      
      // Initialize input values for variables
      const allVars = [
        ...(result.data?.user_input?.input_variables || []),
        ...(result.data?.user_input?.additional_input_variables || [])
      ];
      
      const initial = {};
      allVars.forEach(v => {
        initial[v.name] = v.type === 'Array List' ? [''] : '';
      });
      setInputValues(initial);
      
      setCurrentStep(1);
    } else {
      toast.error('Failed to load project');
    }
  };

  const handleNext = useCallback(() => {
    // Validation
    if (currentStepData.id === 'nameAndVariables') {
      if (!comparisonName.trim()) {
        toast.error('Please enter a comparison name');
        return;
      }
      
      // Validate required input variables
      const requiredVars = selectedProject?.user_input?.input_variables || [];
      const missingVars = requiredVars.filter(v => {
        const val = inputValues[v.name];
        if (v.type === 'Array List') {
          return !val || val.length === 0 || val.every(item => !item?.trim());
        }
        return !val || !val.trim();
      });
      
      if (missingVars.length > 0) {
        toast.error(`Required fields missing: ${missingVars.map(v => v.name).join(', ')}`);
        return;
      }
    }

    if (currentStepData.id === 'modelConfig') {
      if (!config1.model || !config2.model) {
        toast.error('Please select both models');
        return;
      }
    }

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, currentStepData, comparisonName, config1.model, config2.model, selectedProject, inputValues]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const handleRunComparison = () => {
    // Clean input values (remove empty array items)
    const cleanedInputs = {};
    const allVars = [
      ...(selectedProject?.user_input?.input_variables || []),
      ...(selectedProject?.user_input?.additional_input_variables || [])
    ];
    
    allVars.forEach(v => {
      const val = inputValues[v.name];
      if (v.type === 'Array List') {
        const filtered = (val || []).filter(item => item?.trim());
        if (filtered.length > 0) {
          cleanedInputs[v.name] = filtered;
        }
      } else if (val && val.trim()) {
        cleanedInputs[v.name] = val.trim();
      }
    });

    // Pass config back to parent for streaming
    onComplete({
      project: selectedProject,
      comparisonName,
      inputValues: cleanedInputs,
      config1,
      config2
    });
  };

  const isCurrentStepValid = useCallback(() => {
    if (currentStepData.id === 'projectSelect') {
      return !!selectedProject;
    }
    if (currentStepData.id === 'nameAndVariables') {
      return comparisonName.trim();
    }
    if (currentStepData.id === 'modelConfig') {
      return config1.model && config2.model;
    }
    return true;
  }, [currentStepData, selectedProject, comparisonName, config1.model, config2.model]);

  const renderProgressBar = () => (
    <div className="py-5 px-6 bg-bg-tertiary border-b border-border-primary flex-shrink-0">
      <div className="flex justify-between mb-4 relative">
        {WIZARD_STEPS.map((step, index) => (
          <div
            key={step.id}
            className="flex flex-col items-center gap-2 flex-1 relative"
          >
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all duration-300 shadow-sm z-[2] ${index + 1 === currentStep + 1 ? 'bg-primary-600 text-white border-primary-600 ring-4 ring-primary-500/10 scale-105' : index + 1 < currentStep + 1 ? 'bg-primary-600 text-white border-primary-600' : 'bg-bg-secondary text-text-tertiary border-border-primary'}`}>
              {index + 1 < currentStep + 1 ? (
                <span className="text-sm">✓</span>
              ) : (
                <span className="text-[13px]">{index + 1}</span>
              )}
            </div>
            <span className={`text-xs font-medium text-center transition-all duration-fast whitespace-nowrap ${index + 1 === currentStep + 1 ? 'text-text-primary font-semibold' : index + 1 < currentStep + 1 ? 'text-text-secondary' : 'text-text-tertiary'}`}>{step.title}</span>
          </div>
        ))}
      </div>
      <div className="h-[3px] bg-border-primary rounded-full overflow-hidden relative">
        <div 
          className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full transition-[width] duration-[400ms] ease-smooth relative shadow-[0_0_8px_rgba(99,102,241,0.3)]" 
          style={{ width: `${(currentStep / 2) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full h-screen flex justify-center items-center py-6 px-6 bg-bg-primary overflow-hidden">
      <div className="w-full max-w-[850px] h-[calc(100vh-48px)] max-h-[900px] bg-bg-secondary rounded-xl border border-border-primary shadow-2xl overflow-hidden flex flex-col animate-fadeIn">
        
        {/* Project Name Header (shown after selection) */}
        {selectedProject && (
          <div className="flex items-center gap-2 py-4 px-6 bg-gradient-to-b from-bg-tertiary to-bg-secondary border-b border-border-primary flex-shrink-0">
            <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">Project:</span>
            <span className="text-base font-semibold text-text-primary font-mono">{selectedProject.project_name}</span>
          </div>
        )}

        {/* Progress Bar */}
        {renderProgressBar()}

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-0 m-0">
          <div className="flex-1 py-6 px-6 overflow-y-auto overflow-x-hidden min-h-0">
            {currentStepData.id === 'projectSelect' && (
              <StepProjectSelect
                projects={projects}
                searchProject={searchProject}
                setSearchProject={setSearchProject}
                onSelectProject={handleSelectProject}
              />
            )}
            {currentStepData.id === 'nameAndVariables' && (
              <StepNameAndVariables
                comparisonName={comparisonName}
                setComparisonName={setComparisonName}
                requiredVariables={selectedProject?.user_input?.input_variables || []}
                optionalVariables={selectedProject?.user_input?.additional_input_variables || []}
                inputValues={inputValues}
                setInputValues={setInputValues}
              />
            )}
            {currentStepData.id === 'modelConfig' && (
              <StepModelConfig
                config1={config1}
                setConfig1={setConfig1}
                config2={config2}
                setConfig2={setConfig2}
                availableProviders={availableProviders}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center py-4 px-6 border-t border-border-primary bg-bg-secondary flex-shrink-0">
            {!isFirstStep ? (
              <button
                type="button"
                onClick={handleBack}
                className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                onClick={onCancel}
                className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}
            
            {currentStep < WIZARD_STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!isCurrentStepValid()}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleRunComparison}
                disabled={!isCurrentStepValid()}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Run Comparison
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ComparisonWizard.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ComparisonWizard;
